import { useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import type { NavigateFunction } from 'react-router-dom';
import { getSession, User } from './lib/auth-store';
import { useThemeOverrides } from './lib/content-loader';
import { getFlowBuilderUrl } from './lib/config';
import { LandingPage, DesignSystemPage, StatusPage, SovereignAiPage, MarketplacePage } from './features/marketing';
import { DashboardPage, SsoSettingsPage, OrgSecurityPage, OrgDsarPage } from './features/dashboard';
import {
  PrivacyPage,
  TermsPage,
  CookiesPage,
  SecurityPage,
  ContactPage,
  TrustCenterPage,
  SubprocessorsPage,
  AibomPage,
  SlaPage,
  DeprecationPage,
  RegionsPage,
  DsarRequestPage,
} from './features/legal';
import {
  DocsLandingPage,
  GettingStartedPage,
  BrowserTiersPage,
  ApiReferencePage,
  SelfHostingPage,
  PlansPage,
  UserRolesPage,
  DeleteAndRestorePage,
  ScriptStepPage,
  BillingPage,
} from './features/docs';
import { SupportChat } from './components/SupportChat';
import './components/support-chat.css';

/**
 * Adapter that lets the existing public-site components keep their
 * `onNavigate(target: string)` prop signature while we delegate to
 * react-router-dom under the hood.
 *
 * Accepts the legacy string keys ('landing', 'login', 'register',
 * 'forgot-password', 'reset-password?token=X', 'dashboard') so we
 * don't have to touch the marketing page bodies in this pass.
 *
 * Auth keys (`login`, `register`, `forgot-password`, `reset-password`)
 * still hit landing-page `/login` etc. routes; those routes now render
 * `<RedirectToApp>` and bounce the browser onto the canonical
 * flow-builder auth UI at `/app/...`. See `RedirectToApp` below for
 * the full mapping.
 */
function makeLegacyNavigate(navigate: NavigateFunction) {
  return (target: string) => {
    let path: string;
    if (target.startsWith('reset-password?token=')) {
      path = `/reset-password?token=${encodeURIComponent(target.split('=')[1] ?? '')}`;
    } else if (target === 'landing') {
      path = '/';
    } else if (target.startsWith('/')) {
      path = target;
    } else {
      path = `/${target}`;
    }
    navigate(path);
    window.scrollTo(0, 0);
  };
}

/**
 * Single source of truth for "send the visitor to the canonical sign-in
 * UI in flow-builder". Used by the `/login`, `/register`,
 * `/forgot-password`, `/reset-password` and `/auth-success` routes
 * below — every external bookmark, OAuth callback, password-reset
 * email link or "Sign In" CTA on the marketing site flows through here.
 *
 * Why a hard `window.location.replace` and not a react-router
 * `<Navigate>`?
 *   1. landing-page (this SPA) and flow-builder are separate Vite
 *      bundles. In dev they live on different ports (`:3000` vs `:3100`);
 *      in prod they share an origin but are routed by Caddy at `/app/*`.
 *      Either way the redirect crosses an SPA boundary, so we need a
 *      full document load — react-router's same-tree `<Navigate>` would
 *      just rewrite the URL and try (and fail) to find a matching route
 *      in this bundle.
 *   2. `replace()` (vs `assign()`) keeps the user's back button useful
 *      — pressing back after the redirect lands them on whatever
 *      marketing page they came from, not on `/login`.
 *
 * The `view` prop picks an initial state for the canonical
 * `LoginPage` in flow-builder:
 *   - `'login'`     → `/app/` (default; LoginPage opens on the login tab).
 *   - `'register'`  → `/app/?tab=register` (LoginPage flips to the register tab).
 *   - `'forgot'`    → `/app/?view=forgot` (LoginPage opens the forgot-password sub-view).
 *   - `'reset'`     → `/app/#/reset-password?token=…` (existing hash route the
 *                     in-app LoginPage already handles for reset emails).
 *   - `'auth-success'` → `/app/#/auth-success?token=…&redirect=…&provider=…`
 *                     (existing hash route that finishes the OAuth/SSO handshake
 *                     by stashing the JWT).
 *
 * Any `?sso=pending|error&provider=…&reason=…` query the redirector
 * lands on is forwarded to the target so the flow-builder LoginPage
 * can surface the inline notice that the OAuth callback emitted.
 */
function RedirectToApp({ view }: { view: 'login' | 'register' | 'forgot' | 'reset' | 'auth-success' }) {
  const [params] = useSearchParams();
  const location = useLocation();

  useEffect(() => {
    const base = getFlowBuilderUrl().replace(/\/$/, '');

    let target: string;
    if (view === 'login') {
      target = `${base}/`;
    } else if (view === 'register') {
      target = `${base}/?tab=register`;
    } else if (view === 'forgot') {
      target = `${base}/?view=forgot`;
    } else if (view === 'reset') {
      const token = params.get('token') ?? '';
      target = `${base}/#/reset-password?token=${encodeURIComponent(token)}`;
    } else {
      // 'auth-success' — preserve the entire URL fragment unchanged so
      // the JWT + redirect target + provider tag survive the hop.
      const hash = location.hash || '';
      target = `${base}/#/auth-success${hash.replace(/^#/, '?')}`;
    }

    // Preserve sso=pending|error notices so the canonical LoginPage can
    // render them. We don't carry over the token (resets) or fragment
    // (auth-success) — those are encoded into `target` already.
    const carryOver = new URLSearchParams();
    for (const k of ['sso', 'provider', 'reason', 'next']) {
      const v = params.get(k);
      if (v !== null && v !== '') carryOver.append(k, v);
    }
    if (carryOver.toString()) {
      target += (target.includes('?') ? '&' : '?') + carryOver.toString();
    }

    window.location.replace(target);
  }, [view, params, location.hash]);

  return (
    <div
      role="status"
      aria-live="polite"
      style={{ padding: '4rem 1rem', textAlign: 'center', color: 'var(--text-muted, #777)' }}
    >
      Redirecting to sign in…
    </div>
  );
}

function useUser(): [User | null, (user: User | null) => void] {
  const [user, setUser] = useState<User | null>(() => getSession());
  useEffect(() => {
    setUser(getSession());
  }, []);
  return [user, setUser];
}

function LandingRoute() {
  const navigate = useNavigate();
  const legacyNavigate = makeLegacyNavigate(navigate);
  const [user, setUser] = useUser();
  return <LandingPage user={user} onNavigate={legacyNavigate} onLogout={() => setUser(null)} />;
}

function DashboardRoute() {
  const navigate = useNavigate();
  const legacyNavigate = makeLegacyNavigate(navigate);
  const [user, setUser] = useUser();
  if (!user) {
    return <RedirectToApp view="login" />;
  }
  return (
    <DashboardPage
      user={user}
      onNavigate={legacyNavigate}
      onLogout={() => {
        setUser(null);
        legacyNavigate('landing');
      }}
    />
  );
}

function SsoSettingsRoute() {
  const navigate = useNavigate();
  const [user] = useUser();
  if (!user) {
    return <RedirectToApp view="login" />;
  }
  return <SsoSettingsPage onBack={() => navigate('/dashboard')} />;
}

function OrgSecurityRoute() {
  const navigate = useNavigate();
  const [user] = useUser();
  if (!user) {
    return <RedirectToApp view="login" />;
  }
  return <OrgSecurityPage onBack={() => navigate('/dashboard')} />;
}

function OrgDsarRoute() {
  const navigate = useNavigate();
  const [user] = useUser();
  if (!user) {
    return <RedirectToApp view="login" />;
  }
  return <OrgDsarPage onBack={() => navigate('/dashboard')} />;
}

export function App() {
  // Apply runtime accent overrides from public/content/theme.json. No-op when
  // theme.json contains no `light` / `dark` blocks (e.g. before a brand colour
  // is picked from /design-system).
  useThemeOverrides();
  return (
    <>
      <Routes>
        <Route path="/" element={<LandingRoute />} />
        {/*
         * Auth routes intentionally render <RedirectToApp>: the canonical
         * sign-in / register / forgot / reset / OAuth-success UI lives in
         * @rynora/flow-builder at /app/. Keeping these paths live (rather
         * than 404-ing them) means existing inbound links — Google index,
         * "Sign In" CTAs in older content, OAuth callbacks emitted by
         * earlier API revs, password-reset emails sent before the
         * migration — all bounce cleanly to the canonical surface
         * instead of breaking. See RedirectToApp above for the URL
         * mapping and notice-passthrough rules.
         */}
        <Route path="/login" element={<RedirectToApp view="login" />} />
        <Route path="/register" element={<RedirectToApp view="register" />} />
        <Route path="/forgot-password" element={<RedirectToApp view="forgot" />} />
        <Route path="/reset-password" element={<RedirectToApp view="reset" />} />
        <Route path="/auth-success" element={<RedirectToApp view="auth-success" />} />
        <Route path="/dashboard" element={<DashboardRoute />} />
        <Route path="/dashboard/sso" element={<SsoSettingsRoute />} />
        <Route path="/dashboard/security" element={<OrgSecurityRoute />} />
        <Route path="/dashboard/dsar" element={<OrgDsarRoute />} />
        <Route path="/design-system" element={<DesignSystemPage />} />
        <Route path="/sovereign-ai" element={<SovereignAiPage />} />
        <Route path="/marketplace" element={<MarketplacePage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/cookies" element={<CookiesPage />} />
        <Route path="/security" element={<SecurityPage />} />
        <Route path="/trust" element={<TrustCenterPage />} />
        <Route path="/trust/subprocessors" element={<SubprocessorsPage />} />
        <Route path="/trust/regions" element={<RegionsPage />} />
        <Route path="/trust/ai" element={<AibomPage />} />
        <Route path="/trust/sla" element={<SlaPage />} />
        <Route path="/trust/deprecation" element={<DeprecationPage />} />
        <Route path="/privacy/data-request" element={<DsarRequestPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/docs" element={<DocsLandingPage />} />
        <Route path="/docs/getting-started" element={<GettingStartedPage />} />
        <Route path="/docs/browser-tiers" element={<BrowserTiersPage />} />
        <Route path="/docs/api" element={<ApiReferencePage />} />
        <Route path="/docs/self-hosting" element={<SelfHostingPage />} />
        <Route path="/docs/plans" element={<PlansPage />} />
        <Route path="/docs/billing" element={<BillingPage />} />
        <Route path="/docs/user-roles" element={<UserRolesPage />} />
        <Route path="/docs/delete-and-restore" element={<DeleteAndRestorePage />} />
        <Route path="/docs/script-step" element={<ScriptStepPage />} />
        <Route path="/status" element={<StatusPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      {/* Floating "leave a message" support chat. Mounted at the app
          root so it appears on every public page — landing, pricing,
          docs, legal, status, auth — without each route opting in.
          The widget is collapsed by default; clicking the bubble in
          the bottom-right opens the panel. Hidden inside the dashboard
          (post-login) since support there comes through the in-app
          help menu, not the public chat. */}
      <SupportChatGate />
    </>
  );
}

/**
 * Renders the public-site chat bubble on every route except the
 * authenticated dashboard. Kept as a sibling component so the location
 * hook re-runs only when the route changes — the widget itself stays
 * mounted across navigations to preserve the open/closed state.
 */
function SupportChatGate() {
  const { pathname } = useLocation();
  if (pathname.startsWith('/dashboard')) return null;
  return <SupportChat />;
}
